/**
 * Single source-of-truth English content for the public pages
 * (Landing, Blog index, Blog posts, Terms, Privacy, Refund).
 *
 * Translations live in ./translations/{lang}.ts and are produced by
 * scripts/translate-content.mjs (configured AI gateway).
 *
 * Page components import getContent(lang) from "./index" and render the
 * returned object. Strings may contain limited inline HTML (<strong>, <em>,
 * <a href="...">). Pages render them via dangerouslySetInnerHTML — the
 * content is fully under our control.
 */

import whyItMatters from "./why-it-matters.json";

export type LegalSection = { h: string; html: string };

export type LegalPage = {
  title: string;
  lastUpdatedLabel: string;
  intro: string;
  sections: LegalSection[];
};

export type BlogPostContent = {
  slug: string;
  title: string;
  description: string;
  /** Markdown-lite (## / ### / - / **bold**). */
  content: string;
};

export type LandingContent = {
  // Header
  headerTagline: string;
  headerSignIn: string;

  // Hero
  heroEyebrow: string;
  heroTitleA: string; // "Track every match."
  heroTitleB: string; // "Understand"
  heroTitleC: string; // "your tennis."
  heroDesc: string;
  heroCta1: string;
  heroCta2: string;
  heroCta3: string;
  heroBullet1: string;
  heroBullet2: string;
  heroBullet3: string;

  // Why tracking matters
  whyEyebrow: string;
  whyTitle: string;
  whyP1: string;
  whyP2: string;
  whyP3: string;
  whyP4: string;
  whyP5: string;

  // How Tennis Log helps you improve
  howEyebrow: string;
  howTitle: string;
  howIntro: string;
  howB1Title: string; howB1Desc: string;
  howB2Title: string; howB2Desc: string;
  howB3Title: string; howB3Desc: string;
  howB4Title: string; howB4Desc: string;
  howCta: string;

  // What you'll see after 10 matches
  tenEyebrow: string;
  tenTitle: string;
  tenIntro: string;
  tenC1Title: string; tenC1Desc: string;
  tenC2Title: string; tenC2Desc: string;
  tenC3Title: string; tenC3Desc: string;
  tenC4Title: string; tenC4Desc: string;
  tenC5Title: string; tenC5Desc: string;
  tenC6Title: string; tenC6Desc: string;
  tenOutro: string;
  tenCta: string;

  // About
  aboutEyebrow: string;
  aboutTitle: string;
  aboutP1: string;
  aboutP2: string; // contains links to /privacy and /terms
  aboutP3: string;

  // Blog teaser
  blogTeaserEyebrow: string;
  blogTeaserTitle: string;
  blogTeaserDesc: string;
  blogTeaserBtn: string;

  // Guide teaser
  guideEyebrow: string;
  guideTitle: string;
  guideDesc: string;
  guideBtn: string;


  // Hero conversion block (guest frame + trust chips)
  heroNoSignup?: string;
  heroSaveNote?: string;
  heroSignupCta?: string;
  haveAccount?: string;
  haveAccountLink?: string;
  trustChip1?: string;
  trustChip2?: string;

  // "What 25 matches can reveal" section
  revealEyebrow?: string;
  revealTitle?: string;
  revealSubtitle?: string;

  // Mobile progressive disclosure
  readMore?: string;
  readLess?: string;

  // Final CTA
  finalCtaTitle: string;
  finalCtaDesc: string;
  finalCtaBtn1: string;
  finalCtaBtn2: string;

  // SEO
  seoTitle: string;
  seoDesc: string;
};

export type BlogIndexContent = {
  seoTitle: string;
  seoDesc: string;
  eyebrow: string;
  titleA: string; // "Articles for players who"
  titleB: string; // "track their tennis"
  intro: string; // may contain <strong>, <em>
  minLabel?: string; // short "min" suffix on cards
  categories?: Record<string, string>; // EN category name -> localized
};

export type BlogPostUI = {
  allArticles: string;
  by: string;
  minRead: string;
  related: string;
};

export type SiteContent = {
  landing: LandingContent;
  blogIndex: BlogIndexContent;
  blogPostUI: BlogPostUI;
  blogPosts: BlogPostContent[];
  terms: LegalPage;
  privacy: LegalPage;
  refund: LegalPage;
};

// --- Blog post categories (translatable). Keyed by EN category name. ---
export const BLOG_CATEGORIES_EN = [
  "Stats Fundamentals",
  "Reference",
  "Training",
  "Strategy",
  "Tools",
  "Mental Game",
] as const;

// ============================================================================
//  English source — all content below is the canonical version that gets
//  translated into el / es / fr / de / it.
// ============================================================================

export const EN_LANDING: LandingContent = {
  headerTagline: "Log matches. Understand stats. Improve.",
  headerSignIn: "Sign in",

  heroEyebrow: "For amateur tennis players",
  heroTitleA: "Every match tells a story.",
  heroTitleB: "Capture yours.",
  heroTitleC: "",
  heroDesc:
    "Log every match in under a minute and turn your scores into stats, patterns and useful insights about your game.",
  heroCta1: "Start your Tennis Log",
  heroCta2: "See how it works",
  heroCta3: "See a sample match",
  heroBullet1: "Free core features",
  heroBullet2: "Your data stays yours",
  heroBullet3: "Works on phone and web",

  whyEyebrow: "Why this matters",
  // Sourced from ./why-it-matters.json — the single source of truth shared
  // with scripts/prerender-static.mjs. Do not inline the copy here.
  whyTitle: whyItMatters.title,
  whyP1: whyItMatters.paragraphs[0],
  whyP2: whyItMatters.paragraphs[1],
  whyP3: whyItMatters.paragraphs[2],
  whyP4: whyItMatters.paragraphs[3],
  whyP5: whyItMatters.paragraphs[4],


  howEyebrow: "Real benefits",
  howTitle: "How Tennis Log helps you improve",
  howIntro:
    "Tennis Log isn't just a scoreboard. It's a working tool for <strong>tennis match tracking</strong>, <strong>tennis stats</strong> and <strong>tennis analysis</strong> built around the way real club, league and competitive players actually want to think about their game.",
  howB1Title: "Spot patterns you'd never notice",
  howB1Desc:
    "Filter your stats by surface, opponent age, court, weather, time of day. The app surfaces correlations a human can't keep in their head — like a 22-point hold-rate drop in night matches under floodlights.",
  howB2Title: "Turn losses into learning",
  howB2Desc:
    "After every loss, the app shows you which numbers actually drove it: a low first-serve %, a tiebreak you bottled, breaks you couldn't convert. You leave with a target, not a grudge.",
  howB3Title: "AI insights, in plain English",
  howB3Desc:
    "AI analysis is available once you have logged match data and generally becomes more informative as your match history grows. It uses privacy-filtered, allowlisted match statistics, ratings and selected context to surface patterns worth reviewing.",
  howB4Title: "Improve tennis performance over time",
  howB4Desc:
    "Rolling averages, year-over-year comparisons and a confidence band on your win-rate trend show whether you're really getting better — or just facing easier opponents.",
  howCta: "Create your free account",

  tenEyebrow: "Your data, after a few weeks",
  tenTitle: "What you'll see after 10 matches",
  tenIntro:
    "Most players underestimate how quickly a small log starts producing real signal. You don't need a season — you need ten matches. Here's what the dashboard, stats and AI insights show you by then.",
  tenC1Title: "Dashboard at a glance",
  tenC1Desc:
    "Six headline summaries — win rate, sets, games, hold rate, breaks won and Current form. Each one reflects the selected year, format and kind filters, so you can isolate your competitive form from your weekend hits.",
  tenC2Title: "Win-rate trend",
  tenC2Desc:
    "A rolling line that shows how your recorded results change over time, to be interpreted alongside opponent strength, format and other match context. With ten matches it already has enough points to draw a meaningful curve and a confidence band that tightens as you keep logging.",
  tenC3Title: "Stats by surface and opponent",
  tenC3Desc:
    "Head-to-head records, performance by court, by surface, by temperature, by ball state, even by floodlights vs daylight. Slice your tennis statistics however you want — find the conditions you thrive in and the ones that wreck you.",
  tenC4Title: "AI Coach narrative",
  tenC4Desc:
    "A short, written summary of your tennis: \"You hold serve well on hard courts but bleed breaks on clay. Your tiebreak record dropped after match 6 — work on first-serve percentage under pressure.\" Useful, specific, personal.",
  tenC5Title: "Performance ratings",
  tenC5Desc:
    "Score yourself and your opponent on forehand, backhand, serve, return, volley, drop shot, lob, awareness, shot variety, court coverage, stamina, speed and tactics — and watch a radar chart compare both sides over time.",
  tenC6Title: "Smartwatch import",
  tenC6Desc:
    "Upload a screenshot from your watch app and the AI fills calories, duration, average BPM, max BPM and HR zones automatically. Your physical effort becomes part of the same record as your tennis result.",
  tenOutro:
    "Ten matches in, you stop guessing. You can answer questions like <em>\"am I actually better on clay?\"</em>, <em>\"do I close out tiebreaks?\"</em>, <em>\"what happens to my game after 90 minutes?\"</em> with numbers — not feelings. That alone is worth the five minutes a week it takes to keep the log going.",
  tenCta: "Log your first match",

  aboutEyebrow: "About",
  aboutTitle: "Built by a tennis player, for tennis players",
  aboutP1:
    "Tennis Log was created by an amateur player who got tired of forgetting his own matches a week after they happened. Existing apps were either too pro-focused (built for ATP analytics teams), too gimmicky (gamified scoreboards with no real stats), or hidden behind expensive subscriptions. We wanted something different: a fast, beautiful, multilingual tool that takes a minute to log a match and gives back a year of compounding insight.",
  aboutP2:
    "The core app is free today. Tennis Log may support the service with advertising on editorial pages. We don't sell your data. Match history is yours — exportable to CSV anytime, deletable any moment from the Settings page. You can read more in our <a href=\"/privacy\">Privacy Policy</a> and <a href=\"/terms\">Terms</a>.",
  aboutP3:
    "Questions, feedback, feature requests — we read every email. Use the <strong>Contact</strong> button in the footer of any page.",

  blogTeaserEyebrow: "Read the blog",
  blogTeaserTitle: "Long-form guides on tennis stats & strategy",
  blogTeaserDesc:
    "Practical, no-fluff articles for players who track their tennis: holds & breaks, surface differences, mental game, AI analysis, and what really separates players who improve from players who don't.",
  blogTeaserBtn: "Browse all articles",

  guideEyebrow: "Learn more",
  guideTitle: "Tennis Statistics Guide for Amateur Players: What the Numbers Actually Mean",
  guideDesc:
    "A detailed reference to the statistics amateur players are most likely to encounter — what each metric measures, how it is calculated, what question it can help answer, and what it cannot tell you by itself.",
  guideBtn: "Read the guide",


  heroNoSignup: "No sign-up required to get started.",
  heroSaveNote:
    "Start without an account. You'll only create one when you want to save the match.",
  heroSignupCta: "Create a free account",
  haveAccount: "Already have an account?",
  haveAccountLink: "Sign in",
  trustChip1: "Free forever for core features",
  trustChip2: "No credit card required",

  revealEyebrow: "A peek inside Tennis Log",
  revealTitle: "What 25 matches can reveal",
  revealSubtitle:
    "The bigger your log gets, the clearer the patterns in your game become.",

  readMore: "Continue reading",
  readLess: "Show less",

  finalCtaTitle: "Your next match deserves to be logged.",
  finalCtaDesc:
    "Get started in under a minute. Try logging a match without an account, or create your free personal Tennis Log.",
  finalCtaBtn1: "Create my free account",
  finalCtaBtn2: "Read the full guide",

  seoTitle: "Tennis Log — Match Tracker, Stats & AI Insights",
  seoDesc:
    "Free tennis match tracker. Log matches, see win rate, holds & breaks, serve stats and opponent patterns, plus AI insights to help you improve.",
};

export const EN_BLOG_INDEX: BlogIndexContent = {
  seoTitle: "Tennis Blog — Stats, Strategy & Match Analysis | Tennis Log",
  seoDesc:
    "Tennis articles on holds & breaks, win rate trends, surface stats, AI match analysis and the mental game. Practical guides for players who track matches.",
  eyebrow: "Tennis Log Blog · By Tennis Log Team",
  titleA: "Articles for players who",
  titleB: "track their tennis",
  intro:
    "Welcome to the Tennis Log blog — a growing library of original, in-depth guides on tennis statistics, match analysis, the mental game, surface tactics and how to turn a simple match log into measurable improvement on court. Every article is written by the <strong>Tennis Log Team</strong> for amateur, intermediate and competitive players who want to understand <em>why</em> they win and lose — not just count results.",
  minLabel: "min",
  categories: {
    "Stats Fundamentals": "Stats Fundamentals",
    "Reference": "Reference",
    "Training": "Training",
    "Strategy": "Strategy",
    "Tools": "Tools",
    "Mental Game": "Mental Game",
  },
};

export const EN_BLOG_POST_UI: BlogPostUI = {
  allArticles: "All articles",
  by: "By Tennis Log Team",
  minRead: "min read",
  related: "Related articles",
};

// Refund — REWRITTEN to clarify there are no paid features at the moment.
export const EN_REFUND: LegalPage = {
  title: "Refund Policy",
  lastUpdatedLabel: "Last updated",
  intro:
    "<strong>Tennis Log</strong> (also referred to as <em>My Tennis Ledger</em>) is currently offered <strong>free of charge</strong>. There are no paid plans, paid features, in-app purchases or subscriptions on the platform at this time. Because no payment is collected from users, there is nothing to refund.",
  sections: [
    {
      h: "Current status: free service",
      html: "All core features — match logging, stats, AI insights, smartwatch import, multi-language support and data export — are available at no cost. Tennis Log may support the service with advertising on editorial pages. Your match data is yours and can be exported or deleted from the Settings page at any time.",
    },
    {
      h: "Future paid features",
      html: "We may, in the future, introduce optional paid plans with advanced features. If we do, this Refund Policy will be updated <strong>before</strong> the launch of any paid plan to clearly describe the refund window, eligibility, processing time and any other terms. Until that update is published, no payment will be requested from users.",
    },
    {
      h: "Contact",
      html: "Questions about this policy? Email Tennis Log at <a href=\"mailto:tennislogsupport@gmail.com\">tennislogsupport@gmail.com</a>.",
    },
  ],
};

export const EN_TERMS: LegalPage = {
  title: "Terms of Service",
  lastUpdatedLabel: "Last updated",
  intro:
    "These Terms of Service (\"Terms\") govern your use of <strong>My Tennis Ledger</strong> (also known as \"Tennis Log\", \"the App\"), operated by <strong>Tennis Log</strong> (\"we\", \"us\", \"our\"). By creating an account or using the App, you agree to be bound by these Terms.",
  sections: [
    { h: "1. Acceptance", html: "By accessing or using the App you confirm that you have read, understood and agree to these Terms. If you do not agree, please do not use the App. You must be of legal age in your jurisdiction to enter into this agreement." },
    { h: "2. Your Account", html: "You are responsible for keeping your login credentials secure and for all activity under your account. You agree to provide accurate information and to keep it up to date." },
    { h: "3. Your Data", html: "You own all the match data you enter. You can edit, export or delete it at any time from within the App." },
    { h: "4. Acceptable Use", html: "You agree not to misuse the App. In particular you must not: use the App for any unlawful, fraudulent or abusive purpose; infringe the intellectual property or privacy rights of others; upload malware, attempt to probe, scan or breach security; or scrape, reverse engineer or interfere with the normal operation of the App." },
    { h: "5. Intellectual Property", html: "The App's design, code, name and logo are the intellectual property of Tennis Log and are protected by copyright laws. We grant you a limited, non-exclusive, non-transferable right to use the App for your personal, non-commercial use. Unauthorised reproduction or distribution is prohibited." },
    { h: "6. Pricing", html: "The App is currently provided <strong>free of charge</strong>. There are no paid plans or in-app purchases at this time. If we introduce optional paid features in the future, the pricing, billing terms and a refund policy will be presented clearly before any payment is requested." },
    { h: "7. Service Level", html: "We strive to keep the App available but we do not guarantee that it will be uninterrupted, error-free or secure at all times. The App is provided \"as is\" and \"as available\" without warranties of any kind, to the fullest extent permitted by law." },
    { h: "8. Suspension and Termination", html: "We may suspend or terminate your access to the App at any time, with or without notice, if you breach these Terms, pose a security or fraud risk, or repeatedly violate our policies. You may stop using the App and delete your account at any time." },
    { h: "9. Limitation of Liability", html: "To the fullest extent permitted by law, Tennis Log shall not be liable for any indirect, incidental, special or consequential damages, or for any loss of data, profits or goodwill arising from your use of the App." },
    { h: "10. Changes to These Terms", html: "We may update these Terms from time to time. Material changes will be communicated through the App. Continued use of the App after changes become effective constitutes acceptance of the revised Terms." },
    { h: "11. Contact", html: "For any questions about these Terms, contact Tennis Log at <a href=\"mailto:tennislogsupport@gmail.com\">tennislogsupport@gmail.com</a>." },
  ],
};

export const EN_PRIVACY: LegalPage = {
  title: "Privacy Policy",
  lastUpdatedLabel: "Last updated",
  intro:
    "This Privacy Notice describes how <strong>Tennis Log</strong> (\"we\", \"us\", \"our\"), operator of the <strong>My Tennis Ledger</strong> / <strong>Tennis Log</strong> application (\"the App\"), collects and processes personal data. Tennis Log acts as the <strong>data controller</strong> for the personal data described below.",
  sections: [
    { h: "Data We Collect", html: "<strong>Account data</strong> — email address, password hash, optional display name. <br/><strong>Match data</strong> — dates, scores, opponents, surface, notes and other content you enter. <br/><strong>Technical data</strong> — IP address, device/browser identifiers, basic usage telemetry and logs for security and reliability. <br/><strong>Support data</strong> — any messages you send us by email or through the App." },
    { h: "Purposes & Legal Basis", html: "Providing the App and your account — <em>performance of a contract</em>. Securing the App, fraud and abuse prevention — <em>legitimate interests</em>. Responding to support requests — <em>legitimate interests</em> / contract. Improving the product through aggregated usage analysis — <em>legitimate interests</em>. Sending service or administrative emails — <em>contract</em>; marketing emails (if any) — <em>consent</em>. Complying with legal obligations — <em>legal obligation</em>." },
    { h: "Sharing", html: "We share personal data only with hosting and infrastructure providers that store data on our behalf, with professional advisers where necessary, and with public authorities where required by applicable law. We do not sell your personal data." },
    { h: "International Transfers", html: "Some of our service providers may be located outside the UK/EEA. Where personal data is transferred internationally we rely on appropriate safeguards such as Standard Contractual Clauses or adequacy decisions." },
    { h: "Retention", html: "We keep your account and match data for as long as your account is active. If you delete your account, your data is deleted or anonymised within 30 days, except where we are required to retain certain records by law. A scheduled automatic purge then applies fixed windows: contact messages are deleted after 180 days, email delivery logs after 30 days, used unsubscribe tokens 30 days after they are used (unused tokens are kept so unsubscribe links in already delivered emails keep working), and failed queued-email payloads after 30 days. Email suppression (do-not-contact) entries are kept for as long as needed so we do not email you again after an unsubscribe or bounce; their nonessential metadata is cleared after 30 days." },
    { h: "Storage and Security", html: "Data is stored on our backend with row-level security so only you can access your matches. We apply appropriate technical and organisational measures including encryption in transit, access controls and regular updates." },
    { h: "Cookies & Tracking", html: "We use a small number of necessary cookies/local storage entries to keep you signed in and to remember your preferences (such as language and cookie consent). Analytics and advertising cookies are disabled by default and are only loaded after you accept them in the cookie banner, which you can reopen at any time to change or withdraw your choice. Until you consent, advertising-related storage, ad user data and ad personalisation remain denied (Google Consent Mode v2 defaults). Tennis Log may display advertising on some editorial pages, such as the Blog, Tennis Statistics Guide and Academy articles. When advertising storage is denied, Google tags do not set advertising cookies; however, under Google's Advanced Consent Mode, cookieless consent/measurement signals (such as the consent state and non-identifying pings) may still be transmitted to Google. If you consent to marketing cookies, Google and other third-party advertising providers may set cookies or use device identifiers to serve, cap and measure ads, including personalised ads. You can read how Google uses advertising cookies in <a href=\"https://policies.google.com/technologies/ads\" target=\"_blank\" rel=\"noopener\">Google\u2019s advertising policies</a>. When advertising is enabled, and subject to your consent choices and applicable law, Google and other third-party advertising providers may place and read cookies or use web beacons, IP addresses, device identifiers or other identifiers to collect information as a result of ad serving, including to serve, limit the frequency of and measure ads. <a href=\"https://policies.google.com/technologies/partner-sites\" target=\"_blank\" rel=\"noopener\">How Google uses information from sites or apps that use its services</a>." },
    { h: "Your Rights", html: "Depending on your jurisdiction, you have rights to access, rectify, erase, restrict or object to processing of your personal data, and to data portability. You may also withdraw consent at any time and lodge a complaint with your local data protection authority. You can edit or delete most of your data directly within the App." },
    { h: "Health Data (Special Category)", html: "Heart rate, heart-rate zones, calories, activity duration and smartwatch screenshots are health data under Article 9 GDPR. They are <strong>optional</strong> and are processed only on the basis of your <strong>explicit consent</strong>, which is unticked by default, requested before the first use of the smartwatch features, and recorded with the exact version of the consent text you were shown. You can withdraw it at any time in Settings; withdrawal blocks further processing immediately and deletes the health data and smartwatch screenshots already stored." },
    { h: "AI Processing", html: "When you request AI insights, match data is sent to an AI provider through a secure AI processing gateway. Before it leaves the App a privacy filter applies a strict allowlist: names of opponents and partners are replaced with ephemeral labels such as \"opponent_2\", and email addresses, account identifiers and phone numbers are removed. Health data is included only while your explicit health-data consent is active. AI insights are generated on demand and are not stored on our servers." },
    { h: "Data About Other People", html: "Match entries may include limited information about opponents and partners (a name you choose, playing characteristics and your own observations). Collect only what you need for your own analysis. We minimise this data: birth day, birth month and opponent weight are no longer collected. Existing historical entries are preserved unchanged, and you can edit or delete them at any time." },
    { h: "Age Requirement", html: "Tennis Log accounts are intended for people aged <strong>16 or over</strong>. This is our product eligibility rule and is confirmed on every account-creation path. If we learn that an account belongs to someone under 16, we will delete it." },
    { h: "Contact", html: "For privacy questions or to exercise your rights, contact Tennis Log at <a href=\"mailto:tennislogsupport@gmail.com\">tennislogsupport@gmail.com</a>. We aim to respond within one month." },
  ],
};

// Blog posts: re-export the existing English content with a stable shape.
// We import from blogPosts.ts to avoid duplicating the prose.
import { blogPosts as enBlogPosts } from "@/data/blogPosts";

export const EN_BLOG_POSTS: BlogPostContent[] = enBlogPosts.map((p) => ({
  slug: p.slug,
  title: p.title,
  description: p.description,
  content: p.content,
}));

export const EN_CONTENT: SiteContent = {
  landing: EN_LANDING,
  blogIndex: EN_BLOG_INDEX,
  blogPostUI: EN_BLOG_POST_UI,
  blogPosts: EN_BLOG_POSTS,
  terms: EN_TERMS,
  privacy: EN_PRIVACY,
  refund: EN_REFUND,
};
